import {
  ACTIVITY_BUCKETS,
  COMPLETION_STATES,
  DUE_BUCKETS,
  NO_FACET_MATCH,
  boardFilterKeywordSchema,
  formatCustomFieldCriterion,
  parseCustomFieldCriterion,
  type ActivityBucket,
  type BoardMember,
  type CompletionState,
  type CustomFieldCheckboxState,
  type CustomFieldCriterion,
  type CustomFieldDefinition,
  type DueBucket,
  type Label,
} from '@molt/shared';
import { WEB_INTERFACE_MESSAGES, interfaceMessage } from './interface-language';

export type BoardFilterFacet = 'members' | 'labels' | 'due' | 'completion';

export interface BoardFilterSelection {
  readonly members: readonly string[];
  readonly labels: readonly string[];
  readonly due: readonly DueBucket[];
  readonly completion: readonly CompletionState[];
  readonly activity: ActivityBucket | null;
  readonly keyword: string;
  /**
   * `FR-SEARCH-031` — the per-custom-field criteria, flat across every field.
   * OR within one field, AND across fields; the grouping is the server's
   * (`apps/server/src/boards/filter.ts`'s `matchesCustomFieldFacet`) and the
   * panel's, never this array's.
   *
   * ⚠⚠ **SESSION STATE. It belongs in the URL and in `FR-SEARCH-009`'s chips
   * and NOWHERE ELSE.** `FR-SEARCH-031`'s own settled edge case says these
   * criteria are "deliberately **not** written into a saved or shared view",
   * because storing one would need a field identity that survives a rename, a
   * retype and a delete and an option identity that survives an option being
   * removed — and no requirement defines either. `FR-SEARCH-011`'s persisted
   * filter (`boardFilterStateSchema`, `PUT /boards/:id/filter`) therefore
   * carries no member for this facet, and any converter between this shape
   * and that one must DROP this field rather than map it.
   */
  readonly customFields: readonly CustomFieldCriterion[];
}

export interface BoardFilterChip {
  readonly key: string;
  readonly facet: string;
  readonly label: string;
  readonly value: string;
  readonly removeLabel: string;
  readonly remove: (filter: BoardFilterSelection) => BoardFilterSelection;
}

export type BoardFilterChipFacet =
  'member' | 'label' | 'due' | 'completion' | 'activity' | 'keyword';

export interface BoardFilterLabelFormatter {
  readonly facet: (facet: BoardFilterChipFacet) => string;
  readonly chipLabel: (facet: BoardFilterChipFacet, value: string) => string;
  readonly removeLabel: (facet: BoardFilterChipFacet, value: string) => string;
  readonly noMembers: () => string;
  readonly unknownMember: () => string;
  readonly genericMember: () => string;
  readonly noLabels: () => string;
  readonly deletedLabel: () => string;
  readonly labelColour: (colour: Label['colour']) => string;
  readonly colourLabel: (colourName: string) => string;
  readonly dueBucket: (bucket: DueBucket) => string;
  readonly completion: (state: CompletionState) => string;
  readonly activity: (bucket: ActivityBucket) => string;
  /**
   * `FR-SEARCH-031`'s chip. The facet's name is the FIELD's own name — member
   * data, never a catalogue key — so unlike every formatter above it takes two
   * arguments and the caller supplies the first.
   */
  readonly customFieldChipLabel: (field: string, value: string) => string;
  readonly customFieldRemoveLabel: (field: string, value: string) => string;
  readonly customFieldCheckbox: (state: CustomFieldCheckboxState) => string;
  /** A criterion naming an option that has since been removed. */
  readonly customFieldDeletedOption: () => string;
  /** A criterion naming a field the definitions do not (or no longer) carry. */
  readonly customFieldUnknownField: () => string;
}

export type BoardFilterTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

export interface BoardFilterLabelFormatterOptions {
  readonly labelColour?: ((colour: Label['colour']) => string) | undefined;
}

function sourceBoardFilterMessage(
  key: string,
  values?: Record<string, string | number>,
  count?: number,
): string {
  const message = WEB_INTERFACE_MESSAGES[key];
  if (message === undefined) return key;
  return interfaceMessage(message, values, count);
}

export function boardFilterLabelFormatter(
  t: BoardFilterTranslate,
  options: BoardFilterLabelFormatterOptions = {},
): BoardFilterLabelFormatter {
  return {
    facet: (facet) => t(`boardFace.filters.facet.${facet}`),
    chipLabel: (facet, value) => t(`boardFace.filters.chip.${facet}`, { value }),
    removeLabel: (facet, value) =>
      facet === 'keyword'
        ? t('boardFace.filters.remove.keyword')
        : t('boardFace.filters.remove.value', { value }),
    noMembers: () => t('boardFace.filters.members.none'),
    unknownMember: () => t('boardFace.filters.members.assignedFallback'),
    genericMember: () => t('boardFace.filters.members.memberFallback'),
    noLabels: () => t('boardFace.filters.labels.none'),
    deletedLabel: () => t('boardFace.filters.labels.deleted'),
    labelColour: options.labelColour ?? sourceLabelColourName,
    colourLabel: (colourName) =>
      t('boardFace.filters.labels.unnamedColour', { colour: colourName }),
    dueBucket: (bucket) => t(`boardFace.filters.due.${bucket}`),
    completion: (state) => t(`boardFace.filters.completion.${state}`),
    activity: (bucket) => t(`boardFace.filters.activity.${bucket}`),
    customFieldChipLabel: (field, value) =>
      t('boardFace.filters.chip.customField', { field, value }),
    customFieldRemoveLabel: (field, value) =>
      t('boardFace.filters.remove.customField', { field, value }),
    customFieldCheckbox: (state) => t(`boardFace.filters.customField.${state}`),
    customFieldDeletedOption: () => t('boardFace.filters.customField.deletedOption'),
    customFieldUnknownField: () => t('boardFace.filters.customField.unknownField'),
  };
}

export const DEFAULT_BOARD_FILTER_LABEL_FORMATTER: BoardFilterLabelFormatter =
  boardFilterLabelFormatter(sourceBoardFilterMessage);

export const EMPTY_BOARD_FILTER: BoardFilterSelection = {
  members: [],
  labels: [],
  due: [],
  completion: [],
  activity: null,
  keyword: '',
  customFields: [],
};

export function isBoardFilterActive(filter: BoardFilterSelection): boolean {
  return boardFilterActiveCount(filter) > 0;
}

export function boardFilterActiveCount(filter: BoardFilterSelection): number {
  return (
    new Set(filter.members).size +
    new Set(filter.labels).size +
    new Set(filter.due).size +
    new Set(filter.completion).size +
    (filter.activity === null ? 0 : 1) +
    (normaliseKeyword(filter.keyword) === '' ? 0 : 1) +
    normaliseCriteria(filter.customFields).length
  );
}

export function boardFilterCacheKey(filter: BoardFilterSelection): string {
  return boardFilterSearchParams(filter).toString();
}

export function boardFilterSearchParams(filter: BoardFilterSelection): URLSearchParams {
  const params = new URLSearchParams();
  for (const member of normalise(filter.members)) params.append('members', member);
  for (const label of normalise(filter.labels)) params.append('labels', label);
  for (const bucket of normalise(filter.due)) params.append('due', bucket);
  for (const state of normalise(filter.completion)) {
    params.append('completion', state);
  }
  if (filter.activity !== null) params.set('activity', filter.activity);
  const keyword = normaliseKeyword(filter.keyword);
  if (keyword !== '') params.set('keyword', keyword);
  /**
   * ⚠ Emitted through `normaliseCriteria`, which SORTS by wire string, for
   * `boardFilterCacheKey`'s sake: this function is the cache key, so two
   * selections differing only in the order the member ticked them would fetch
   * and store the identical board under two entries. Display order — "the
   * definitions' own order" — is the panel's and comes from the definitions
   * array, never from here.
   */
  for (const criterion of normaliseCriteria(filter.customFields)) {
    params.append('customField', formatCustomFieldCriterion(criterion));
  }
  return params;
}

export function boardFilterFromSearch(
  search: string | URLSearchParams,
): BoardFilterSelection {
  const params = new URLSearchParams(search);
  return normaliseFilter({
    members: params.getAll('members'),
    labels: params.getAll('labels'),
    due: params.getAll('due') as DueBucket[],
    completion: params.getAll('completion') as CompletionState[],
    activity: uniqueActivityValue(params.getAll('activity')),
    keyword: params.get('keyword') ?? '',
    /**
     * ⚠ An unparseable value is DROPPED, never carried through as a string.
     * The route builds the next querystring from this shape, so a value the
     * server would 422 must not survive a round trip — a single hand-edited
     * `?customField=` would otherwise make every subsequent board read fail.
     */
    customFields: params
      .getAll('customField')
      .map((raw) => parseCustomFieldCriterion(raw))
      .filter((criterion): criterion is CustomFieldCriterion => criterion !== null),
  });
}

export function withBoardFilterSearchParams(
  search: string | URLSearchParams,
  filter: BoardFilterSelection,
): URLSearchParams {
  const params = new URLSearchParams(search);
  params.delete('members');
  params.delete('labels');
  params.delete('due');
  params.delete('completion');
  params.delete('activity');
  params.delete('keyword');
  params.delete('customField');

  for (const [key, value] of boardFilterSearchParams(filter)) {
    params.append(key, value);
  }

  return params;
}

export function pruneBoardFilterFacets(
  filter: BoardFilterSelection,
  available: {
    readonly memberIds?: readonly string[] | undefined;
    readonly labelIds: readonly string[];
    /**
     * The board's custom field DEFINITIONS as the server answered them —
     * hidden ones included, because it is this function that decides a hidden
     * field offers no facet.
     *
     * ⚠⚠ **`undefined` means "the definitions are not known yet" and imposes
     * NO pruning**, exactly as `memberIds` above already does. The
     * definitions arrive on their own query, separate from the board's
     * contents, so a caller that passed `[]` while that query was still in
     * flight would wipe every criterion a shared URL carried on first paint —
     * the mirror image of the bug `FR-SEARCH-031` AC5 forbids, and just as
     * silent.
     */
    readonly customFields?: readonly CustomFieldDefinition[] | undefined;
  },
): BoardFilterSelection {
  const memberIds =
    available.memberIds === undefined
      ? undefined
      : new Set([...available.memberIds, NO_FACET_MATCH]);
  const labelIds = new Set([...available.labelIds, NO_FACET_MATCH]);
  const normalised = normaliseFilter(filter);

  return {
    ...normalised,
    members:
      memberIds === undefined
        ? normalised.members
        : normalised.members.filter((id) => memberIds.has(id)),
    labels: normalised.labels.filter((id) => labelIds.has(id)),
    customFields:
      available.customFields === undefined
        ? normalised.customFields
        : normalised.customFields.filter((criterion) =>
            criterionIsOfferable(criterion, available.customFields ?? []),
          ),
  };
}

/**
 * Could the panel offer this criterion as a control right now? —
 * `FR-SEARCH-031` AC5's whole test, stated once.
 *
 * ⚠⚠ **AC5 IS WHY THIS EXISTS, AND WHY IT PRUNES RATHER THAN JUST HIDING.**
 * *"Given a field is hidden after a filter on it was applied, when the panel
 * reloads, then that facet is gone AND its criterion no longer constrains the
 * board."* Dropping only the control would leave the board narrowed by an
 * invisible filter, with no chip and no way to remove it — a board that is
 * missing cards for no visible reason, with nothing red anywhere. Pruning the
 * criterion is what makes the second half of that sentence true, and the
 * route's prune effect is what rewrites the URL so it does not come back on
 * the next reload.
 *
 * Four ways a criterion stops being offerable, all treated alike because the
 * member sees the same thing in every one of them — no control:
 *
 *   1. the field is **hidden** (`FR-CARD-027`, and AC5 itself);
 *   2. the field was **deleted**, so no definition carries its id;
 *   3. the field was **retyped**, so the operator no longer applies to it —
 *      a `text` criterion on a field that is now a dropdown is a chip that
 *      matches nothing and cannot be re-created from the panel;
 *   4. a dropdown's **option was removed**, so the option id names nothing.
 *
 * `apps/server/src/boards/filter.ts`'s arm makes cases 1, 2 and a third-board
 * criterion inert on the server as well. The two halves are deliberately not
 * one: the server's makes the criterion harmless for every caller including a
 * cold-loaded shared link, this one removes the chip and the URL parameter.
 */
function criterionIsOfferable(
  criterion: CustomFieldCriterion,
  definitions: readonly CustomFieldDefinition[],
): boolean {
  const definition = definitions.find((item) => item.id === criterion.fieldId);
  if (definition === undefined || definition.hidden) return false;

  switch (criterion.kind) {
    case 'checkbox':
      return definition.type === 'checkbox';
    case 'option':
      return (
        definition.type === 'dropdown' &&
        definition.options.some((option) => option.id === criterion.optionId)
      );
    case 'date':
      return definition.type === 'date';
    case 'text':
      return definition.type === 'text';
    case 'number':
      return definition.type === 'number';
  }
}

/**
 * Add a criterion, or remove it if it is already selected — the custom-field
 * facet's `toggleBoardFilterValue`.
 *
 * Identity is the WIRE STRING, never object identity: two structurally equal
 * criteria are never `===`, so a `Set` of the objects would never find the one
 * already selected and every click would add a duplicate.
 */
export function toggleBoardFilterCriterion(
  filter: BoardFilterSelection,
  criterion: CustomFieldCriterion,
): BoardFilterSelection {
  const wire = formatCustomFieldCriterion(criterion);
  const current = normaliseCriteria(filter.customFields);
  const without = current.filter((item) => formatCustomFieldCriterion(item) !== wire);

  return {
    ...normaliseFilter(filter),
    customFields:
      without.length === current.length
        ? normaliseCriteria([...current, criterion])
        : without,
  };
}

/** Is this exact criterion selected? The panel's `checked` for every control. */
export function isBoardFilterCriterionSelected(
  filter: BoardFilterSelection,
  criterion: CustomFieldCriterion,
): boolean {
  const wire = formatCustomFieldCriterion(criterion);
  return filter.customFields.some((item) => formatCustomFieldCriterion(item) === wire);
}

/**
 * The free-text and numeric operators are not toggles — one value per field at
 * a time, replaced as the member types, and cleared by an empty box. So they
 * get a setter rather than a toggle: `criterion === null` removes whatever
 * criterion of that kind the field held.
 */
export function setBoardFilterCriterion(
  filter: BoardFilterSelection,
  fieldId: string,
  kind: CustomFieldCriterion['kind'],
  criterion: CustomFieldCriterion | null,
): BoardFilterSelection {
  const kept = normaliseCriteria(filter.customFields).filter(
    (item) => item.fieldId !== fieldId || item.kind !== kind,
  );

  return {
    ...normaliseFilter(filter),
    customFields: normaliseCriteria(criterion === null ? kept : [...kept, criterion]),
  };
}

export function toggleBoardFilterValue(
  filter: BoardFilterSelection,
  facet: BoardFilterFacet,
  value: string,
): BoardFilterSelection {
  const next = new Set<string>(filter[facet]);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return {
    members: facet === 'members' ? normalise([...next]) : normalise(filter.members),
    labels: facet === 'labels' ? normalise([...next]) : normalise(filter.labels),
    due:
      facet === 'due'
        ? (normalise([...next]) as DueBucket[])
        : (normalise(filter.due) as DueBucket[]),
    completion:
      facet === 'completion'
        ? (normalise([...next]) as CompletionState[])
        : (normalise(filter.completion) as CompletionState[]),
    activity: filter.activity,
    keyword: normaliseKeyword(filter.keyword),
    customFields: normaliseCriteria(filter.customFields),
  };
}

export function setBoardFilterLabels(
  filter: BoardFilterSelection,
  labels: readonly string[],
): BoardFilterSelection {
  return {
    ...normaliseFilter(filter),
    labels: normalise(labels),
  };
}

export function toggleMyCardsFilter(
  filter: BoardFilterSelection,
  currentUserId: string,
): BoardFilterSelection {
  const currentMembers = normalise(filter.members);
  const nextMembers =
    currentMembers.length === 1 && currentMembers[0] === currentUserId
      ? []
      : [currentUserId];
  return {
    ...normaliseFilter(filter),
    members: nextMembers,
  };
}

export function setBoardFilterActivity(
  filter: BoardFilterSelection,
  activity: ActivityBucket | null,
): BoardFilterSelection {
  return {
    ...normaliseFilter(filter),
    activity,
  };
}

export function setBoardFilterKeyword(
  filter: BoardFilterSelection,
  keyword: string,
): BoardFilterSelection {
  return {
    ...normaliseFilter(filter),
    keyword: normaliseKeyword(keyword),
  };
}

export function removeBoardFilterChip(
  filter: BoardFilterSelection,
  chip: BoardFilterChip,
): BoardFilterSelection {
  return chip.remove(filter);
}

export function boardFilterChips(
  filter: BoardFilterSelection,
  members: readonly BoardMember[],
  labels: readonly Label[],
  customFields: readonly CustomFieldDefinition[] = [],
  formatter: BoardFilterLabelFormatter = DEFAULT_BOARD_FILTER_LABEL_FORMATTER,
): BoardFilterChip[] {
  const chips: BoardFilterChip[] = [];

  for (const id of normalise(filter.members)) {
    const label = memberFilterLabel(id, members, formatter);
    chips.push({
      key: `members:${id}`,
      facet: formatter.facet('member'),
      label: formatter.chipLabel('member', label),
      value: label,
      removeLabel: formatter.removeLabel('member', label),
      remove: (current) => ({
        ...normaliseFilter(current),
        members: normalise(current.members.filter((value) => value !== id)),
      }),
    });
  }

  for (const id of normalise(filter.labels)) {
    const label = labelFilterLabel(id, labels, formatter);
    chips.push({
      key: `labels:${id}`,
      facet: formatter.facet('label'),
      label: formatter.chipLabel('label', label),
      value: label,
      removeLabel: formatter.removeLabel('label', label),
      remove: (current) => ({
        ...normaliseFilter(current),
        labels: normalise(current.labels.filter((value) => value !== id)),
      }),
    });
  }

  for (const bucket of normalise(filter.due) as DueBucket[]) {
    const label = dueBucketFilterLabel(bucket, formatter);
    chips.push({
      key: `due:${bucket}`,
      facet: formatter.facet('due'),
      label: formatter.chipLabel('due', label),
      value: label,
      removeLabel: formatter.removeLabel('due', label),
      remove: (current) => ({
        ...normaliseFilter(current),
        due: normalise(current.due.filter((value) => value !== bucket)) as DueBucket[],
      }),
    });
  }

  for (const state of normalise(filter.completion) as CompletionState[]) {
    const label = completionFilterLabel(state, formatter);
    chips.push({
      key: `completion:${state}`,
      facet: formatter.facet('completion'),
      label: formatter.chipLabel('completion', label),
      value: label,
      removeLabel: formatter.removeLabel('completion', label),
      remove: (current) => ({
        ...normaliseFilter(current),
        completion: normalise(
          current.completion.filter((value) => value !== state),
        ) as CompletionState[],
      }),
    });
  }

  if (filter.activity !== null) {
    const label = activityBucketFilterLabel(filter.activity, formatter);
    chips.push({
      key: `activity:${filter.activity}`,
      facet: formatter.facet('activity'),
      label: formatter.chipLabel('activity', label),
      value: label,
      removeLabel: formatter.removeLabel('activity', label),
      remove: (current) => ({ ...normaliseFilter(current), activity: null }),
    });
  }

  /**
   * `FR-SEARCH-031` through `FR-SEARCH-009` — one chip per criterion, so a
   * custom-field criterion is as removable as every other facet's. AC5's
   * "the facet is gone" removes the CONTROL; without a chip a criterion whose
   * control is present but scrolled out of view would still have no visible
   * handle, which is the failure `FR-SEARCH-009` exists to prevent for the
   * six facets that came before this one.
   *
   * ⚠ The chips are emitted in the DEFINITIONS' order, not the criteria's, so
   * the chip strip reads in the same order as the panel — `FR-SEARCH-031`'s
   * "in the definitions' own order" applied to the second surface that shows
   * them. A criterion whose field is not in `customFields` (not loaded yet, or
   * deleted) still gets a chip, at the end, labelled as an unknown field:
   * `pruneBoardFilterFacets` is what removes it once the definitions are
   * known, and until then a chip the member can click is strictly better than
   * a constraint with no handle.
   */
  const fieldOrder = new Map(customFields.map((field, index) => [field.id, index]));
  const orderedCriteria = [...normaliseCriteria(filter.customFields)].sort(
    (left, right) =>
      (fieldOrder.get(left.fieldId) ?? customFields.length) -
      (fieldOrder.get(right.fieldId) ?? customFields.length),
  );

  for (const criterion of orderedCriteria) {
    const wire = formatCustomFieldCriterion(criterion);
    const definition = customFields.find((item) => item.id === criterion.fieldId);
    const fieldName =
      definition === undefined ? formatter.customFieldUnknownField() : definition.name;
    const value = customFieldCriterionLabel(criterion, customFields, formatter);
    chips.push({
      key: `customField:${wire}`,
      facet: fieldName,
      label: formatter.customFieldChipLabel(fieldName, value),
      value,
      removeLabel: formatter.customFieldRemoveLabel(fieldName, value),
      remove: (current) => ({
        ...normaliseFilter(current),
        customFields: normaliseCriteria(current.customFields).filter(
          (item) => formatCustomFieldCriterion(item) !== wire,
        ),
      }),
    });
  }

  const keyword = normaliseKeyword(filter.keyword);
  if (keyword !== '') {
    chips.push({
      key: `keyword:${keyword}`,
      facet: formatter.facet('keyword'),
      label: formatter.chipLabel('keyword', keyword),
      value: keyword,
      removeLabel: formatter.removeLabel('keyword', keyword),
      remove: (current) => ({ ...normaliseFilter(current), keyword: '' }),
    });
  }

  return chips;
}

/**
 * One criterion as a member reads it — the chip's value and the panel's own
 * option label come from this one function, so the two can never disagree.
 *
 * ⚠ **A dropdown criterion resolves through the OPTION's current label, and
 * that is the whole reason the wire form carries the option's id rather than
 * its text.** An option renamed while a filter is active keeps matching the
 * cards that hold it and re-labels its own chip; a label-valued criterion
 * would silently stop matching them instead.
 */
export function customFieldCriterionLabel(
  criterion: CustomFieldCriterion,
  definitions: readonly CustomFieldDefinition[],
  formatter: BoardFilterLabelFormatter = DEFAULT_BOARD_FILTER_LABEL_FORMATTER,
): string {
  switch (criterion.kind) {
    case 'checkbox':
      return formatter.customFieldCheckbox(criterion.state);
    case 'date':
      return formatter.dueBucket(criterion.bucket);
    case 'text':
      return criterion.text;
    case 'number':
      return String(criterion.value);
    case 'option': {
      const option = definitions
        .find((item) => item.id === criterion.fieldId)
        ?.options.find((item) => item.id === criterion.optionId);
      return option === undefined ? formatter.customFieldDeletedOption() : option.label;
    }
  }
}

export function memberFilterLabel(
  id: string,
  members: readonly BoardMember[],
  formatter: BoardFilterLabelFormatter = DEFAULT_BOARD_FILTER_LABEL_FORMATTER,
): string {
  if (id === NO_FACET_MATCH) return formatter.noMembers();
  const member = members.find((item) => item.id === id);
  if (member === undefined) return formatter.unknownMember();
  if (member.displayName !== '') return member.displayName;
  if (member.username !== '') return `@${member.username}`;
  return formatter.genericMember();
}

export function labelFilterLabel(
  id: string,
  labels: readonly Label[],
  formatter: BoardFilterLabelFormatter = DEFAULT_BOARD_FILTER_LABEL_FORMATTER,
): string {
  if (id === NO_FACET_MATCH) return formatter.noLabels();
  const label = labels.find((item) => item.id === id);
  if (label === undefined) return formatter.deletedLabel();
  if (label.name !== null && label.name !== '') return label.name;
  return formatter.colourLabel(formatter.labelColour(label.colour));
}

export function dueBucketFilterLabel(
  bucket: DueBucket,
  formatter: BoardFilterLabelFormatter = DEFAULT_BOARD_FILTER_LABEL_FORMATTER,
): string {
  return formatter.dueBucket(bucket);
}

export function completionFilterLabel(
  state: CompletionState,
  formatter: BoardFilterLabelFormatter = DEFAULT_BOARD_FILTER_LABEL_FORMATTER,
): string {
  return formatter.completion(state);
}

export function activityBucketFilterLabel(
  bucket: ActivityBucket,
  formatter: BoardFilterLabelFormatter = DEFAULT_BOARD_FILTER_LABEL_FORMATTER,
): string {
  return formatter.activity(bucket);
}

export function isActivityBucket(value: string): value is ActivityBucket {
  return ACTIVITY_BUCKETS.includes(value as ActivityBucket);
}

function isDueBucket(value: string): value is DueBucket {
  return DUE_BUCKETS.includes(value as DueBucket);
}

function isCompletionState(value: string): value is CompletionState {
  return COMPLETION_STATES.includes(value as CompletionState);
}

function uniqueActivityValue(values: readonly string[]): ActivityBucket | null {
  const [value] = normalise(values.filter((candidate) => candidate !== ''));
  return value !== undefined && isActivityBucket(value) ? value : null;
}

function normalise(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value !== ''))].sort();
}

function normaliseFilter(filter: BoardFilterSelection): BoardFilterSelection {
  return {
    members: normalise(filter.members),
    labels: normalise(filter.labels),
    due: normalise(filter.due).filter(isDueBucket),
    completion: normalise(filter.completion).filter(isCompletionState),
    activity: filter.activity,
    keyword: normaliseKeyword(filter.keyword),
    customFields: normaliseCriteria(filter.customFields),
  };
}

/**
 * Deduplicate and order the criteria — `normalise`'s counterpart for a facet
 * whose options are objects rather than strings.
 *
 * By the WIRE STRING in both cases. Two structurally equal criteria are never
 * `===`, so a `Set` of the objects deduplicates nothing; and sorting is what
 * makes `boardFilterCacheKey` independent of the order the member ticked
 * things, which is the same property `normalise`'s own `.sort()` buys for the
 * four string facets.
 */
function normaliseCriteria(
  criteria: readonly CustomFieldCriterion[],
): CustomFieldCriterion[] {
  const byWire = new Map<string, CustomFieldCriterion>();
  for (const criterion of criteria) {
    byWire.set(formatCustomFieldCriterion(criterion), criterion);
  }
  return [...byWire.entries()]
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    .map(([, criterion]) => criterion);
}

function normaliseKeyword(value: string): string {
  const parsed = boardFilterKeywordSchema.safeParse(value);
  return parsed.success ? parsed.data : '';
}

function sourceLabelColourName(colour: Label['colour']): string {
  if (colour === 'colourless') return 'Colourless';

  const [family = '', tier] = colour.split('_');
  const words = tier === undefined ? family : `${tier} ${family}`;
  return words.charAt(0).toUpperCase() + words.slice(1);
}
